/**
 * Starile in care poate fi o scoala fata de editia curenta, asa cum le intoarce
 * `GET /api/schools/registration-status` (`SchoolRegistrationStatusDto.status`).
 */
export type StatusInscriereScoala =
  | 'LIBERA'
  | 'APROBATA_EDITIA_CURENTA'
  | 'IN_ASTEPTARE_EDITIA_CURENTA'
  | 'EDITIE_ANTERIOARA';

/**
 * Stari care deschid un DIALOG. `EDITIE_ANTERIOARA` nu intra aici: ramane panou inline.
 */
export type StatusCuDialogScoalaOcupata = 'APROBATA_EDITIA_CURENTA' | 'IN_ASTEPTARE_EDITIA_CURENTA';

/**
 * `blocant` a existat pana pe 18.09 (APROBATA_EDITIA_CURENTA nu se putea trimite deloc). Owner-ul a
 * decis ca o scoala deja aprobata sa primeasca tot o cerere de preluare, arbitrata de echipa —
 * deci NICIUN status nu mai blocheaza din frontend, iar tipul pastreaza doar cele doua forme reale.
 */
export type FormaDialogScoalaOcupata = 'informativ';

export interface InfoDialogScoalaOcupata {
  forma: FormaDialogScoalaOcupata;
  titlu: string;
  mesaj: string;
}

/** `null` cand statusul nu declanseaza niciun dialog (LIBERA, EDITIE_ANTERIOARA, undefined, null). */
export function formaDialogScoalaOcupata(
  status: StatusInscriereScoala | null | undefined
): FormaDialogScoalaOcupata | null {
  if (status === 'APROBATA_EDITIA_CURENTA' || status === 'IN_ASTEPTARE_EDITIA_CURENTA') return 'informativ';
  return null;
}

/**
 * Cele doua recomandari, in aceeasi formulare pe toate ecranele de «scoala ocupata». Contactul
 * (nume + telefon) NU e in text: vine din variabile de build si se randeaza separat, doar cand exista.
 */
const RECOMANDARI = 'Vă recomandăm să discutați cu dânsul/dânsa sau să contactați echipa Ecotic';

/** Propozitie de sine statatoare: punct la final, contactul echipei pe rândul urmator. */
const RECOMANDARI_PUNCT = `${RECOMANDARI}.`;

/**
 * Varianta din panoul inline (decizie owner, 20.09): fara punct, cu « : » dupa «Ecotic», pentru ca
 * numele si telefonul sa continue aceeasi fraza, in ACELASI paragraf. Se foloseste doar cand rândul
 * de contact chiar se randeaza — altfel rămâne un colon atârnat, fara nimic dupa el.
 */
const RECOMANDARI_INLINE = `${RECOMANDARI} :`;

export function infoDialogScoalaOcupata(
  status: StatusCuDialogScoalaOcupata,
  numeProfesor: string | null | undefined
): InfoDialogScoalaOcupata {
  const nume = numeProfesor?.trim() || 'profesorul înregistrat anterior';

  if (status === 'APROBATA_EDITIA_CURENTA') {
    return {
      forma: 'informativ',
      titlu: 'Școala are deja coordonator',
      mesaj: `Școala are deja coordonator: ${nume}. ${RECOMANDARI_PUNCT}`,
    };
  }

  // Fara «echipa ECOTIC decide» (cerinta clientului, 07.10): trimiterea la o echipa fara nume ii
  // facea pe profesori sa sune peste tot. Persoana de contact se randeaza pe randul urmator, din
  // variabilele de build — vezi `textContactCerereInAsteptare`.
  return {
    forma: 'informativ',
    titlu: 'Există deja o cerere pentru această școală',
    mesaj: `${nume} a trimis deja o cerere pentru această școală.`,
  };
}

/** Bucatile randului de contact; componenta pune numele in bold si telefonul intr-un link `tel:`. */
export interface TextContactCerereInAsteptare {
  inainte: string;
  nume: string;
  mijloc: string;
  telefon: string;
  dupa: string;
}

/**
 * Randul de contact din dialogul «Există deja o cerere pentru această școală», in formularea
 * ceruta de client (07.10), verbatim:
 * «Contactați <nume> - responsabil proiect - <telefon> pentru informații suplimentare».
 *
 * Numele si telefonul vin din variabilele de build (nu stau in cod); cand lipseste unul, propozitia
 * se strange in jurul celuilalt, iar fara niciunul nu se randeaza nimic (`null`).
 */
export function textContactCerereInAsteptare(
  contact: { nume: string; telefon: string } | null | undefined
): TextContactCerereInAsteptare | null {
  const nume = contact?.nume?.trim() ?? '';
  const telefon = contact?.telefon?.trim() ?? '';
  if (!nume && !telefon) return null;

  const dupa = ' pentru informații suplimentare';
  if (nume && telefon) {
    return { inainte: 'Contactați ', nume, mijloc: ' - responsabil proiect - ', telefon, dupa };
  }
  if (nume) return { inainte: 'Contactați ', nume, mijloc: ' - responsabil proiect', telefon: '', dupa };
  return { inainte: 'Contactați responsabilul de proiect - ', nume: '', mijloc: '', telefon, dupa };
}

/**
 * Starile in care formularul se STRANGE sub butonul «Depune totuși cererea» (decizie owner, 18.09):
 * scoala a participat in editiile trecute, sau are deja coordonator aprobat acum. Nu e cazul pentru
 * `IN_ASTEPTARE_EDITIA_CURENTA` (dialog informativ, «Continuă oricum») si nici pentru `LIBERA`.
 */
export function esteScoalaOcupataCuFormularStrans(status: StatusInscriereScoala | null | undefined): boolean {
  return status === 'EDITIE_ANTERIOARA' || status === 'APROBATA_EDITIA_CURENTA';
}

/** Formularul e strans cat timp scoala e ocupata si omul n-a apasat «Depune totuși cererea». */
export function formularStrans(
  status: StatusInscriereScoala | null | undefined,
  aApasatDepuneTotusi: boolean
): boolean {
  return esteScoalaOcupataCuFormularStrans(status) && !aApasatDepuneTotusi;
}

export interface TextPanouScoalaOcupata {
  titlu: string;
  mesaj: string;
}

/**
 * Textul panoului inline pentru scoala cu participare in editiile TRECUTE. Fraza «Puteți totuși
 * depune cererea» a disparut (decizie owner, 18.09): permisiunea o da butonul, nu textul.
 *
 * Fara numele profesorului, propozitia nu poate zice «cu profesorul profesorul înregistrat
 * anterior» — cade pe «cu un alt profesor».
 *
 * `areContact` (decizie owner, 20.09): cand rândul de contact se randeaza, recomandarea se termina
 * cu « : » si numele + telefonul continua aceeasi fraza; cand nu, se termina cu punct. Deci
 * punctuatia depinde de ce urmeaza dupa text, si nu poate fi aleasa inauntrul functiei.
 */
export function textPanouEditieAnterioara(
  numeProfesor: string | null | undefined,
  areContact: boolean
): TextPanouScoalaOcupata {
  const nume = numeProfesor?.trim();
  const cine = nume ? `cu profesorul ${nume}` : 'cu un alt profesor';
  return {
    titlu: 'Școala a participat în edițiile trecute',
    mesaj: `În edițiile trecute, școala a participat ${cine}. ${
      areContact ? RECOMANDARI_INLINE : RECOMANDARI_PUNCT
    }`,
  };
}

/**
 * Textul panoului inline pentru o scoala care are DEJA coordonator aprobat in editia curenta —
 * acelasi mesaj ca in dialog, ca omul care a inchis dialogul sa-l regaseasca sub selector.
 */
export function textPanouScoalaAprobata(numeProfesor: string | null | undefined): TextPanouScoalaOcupata {
  const { titlu, mesaj } = infoDialogScoalaOcupata('APROBATA_EDITIA_CURENTA', numeProfesor);
  return { titlu, mesaj };
}
